import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Drawer from "../../../Component/Drawer";
import InvoiceDocument from "./InvoiceDocument";
import InvoiceCommissionModal from "./InvoiceCommissionModal";
import { ApiInvoice, customerName, inr2, invoiceNo, invoiceTotals, planTypeInfo } from "../invoiceUtils";

interface InvoiceDrawerProps {
  invoice: ApiInvoice | null;
  // open the print dialog as soon as the drawer renders (row menu → Download PDF)
  autoPrint?: boolean;
  onPrinted?: () => void;
  onClose: () => void;
  // hide the Customer button (already on the customer details page)
  hideCustomer?: boolean;
}

// Opens the invoice in a new window with the app's styles and triggers print (→ Save as PDF)
const printElement = (el: HTMLElement, title: string) => {
  const w = window.open("", "_blank");
  if (!w) {
    toast.error("Popup blocked — allow popups to download the PDF");
    return;
  }
  const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).map(n => n.outerHTML).join("\n");
  w.document.write(`<html><head><title>${title}</title>${styles}<style>body{background:#fff;padding:24px}.cx-invd{box-shadow:none;border:none;max-width:760px;margin:0 auto}</style></head><body><div class="cx">${el.outerHTML}</div><script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`);
  w.document.close();
};

const fileTitle = (i: ApiInvoice) => `invoice-${invoiceNo(i).replace(/[\\/#]/g, "-")}`;

const InvoiceDrawer = ({ invoice, autoPrint, onPrinted, onClose, hideCustomer }: InvoiceDrawerProps) => {
  const navigate = useNavigate();
  const docRef = useRef<HTMLDivElement>(null);
  const [showCommission, setShowCommission] = useState(false);

  const print = () => {
    if (invoice && docRef.current) printElement(docRef.current, fileTitle(invoice));
  };

  useEffect(() => {
    if (invoice && autoPrint && docRef.current) {
      printElement(docRef.current, fileTitle(invoice));
      onPrinted?.();
    }
  }, [invoice, autoPrint, onPrinted]);

  if (!invoice) return null;
  const t = invoiceTotals(invoice);
  const userId = invoice.invoice_user_id?._id;

  return (
    <Drawer
      show
      wide
      tintBody
      onClose={onClose}
      title={<span className="mono">{invoiceNo(invoice)}</span>}
      subtitle={`${customerName(invoice)} · ${planTypeInfo(invoice.invoice_plan_type)[0]} · ${inr2(t.tot)}`}
      footer={<>
        {userId && !hideCustomer && (
          <button type="button" className="cx-btn cx-btn-light" onClick={() => navigate(`/customers-and-users/view-user/${userId}`)}>
            <i className="ri-building-2-line"></i>Customer
          </button>
        )}
        {userId && invoice.invoice_plan_type === "wallet_topup" && (
          <button type="button" className="cx-btn cx-btn-light" onClick={() => { onClose(); navigate(`/wallet?client=${userId}`); }}>
            <i className="ri-wallet-3-line"></i>Wallet top-ups
          </button>
        )}
        <button type="button" className="cx-btn cx-btn-light" onClick={() => setShowCommission(true)}><i className="ri-hand-coin-line"></i>Commission</button>
        <div className="sp"></div>
        <button type="button" className="cx-btn cx-btn-primary" onClick={print}><i className="ri-download-2-line"></i>Download PDF</button>
      </>}
    >
      <InvoiceDocument invoice={invoice} ref={docRef} />
      <InvoiceCommissionModal invoice={invoice} show={showCommission} onHide={() => setShowCommission(false)} />
    </Drawer>
  );
};

export default InvoiceDrawer;
